import {
  H1,
  HTMLSelect,
  FormGroup,
  Button,
  FileInput,
  MenuItem,
  Spinner,
  Switch,
  Icon,
} from '@blueprintjs/core'
import { MultiSelect2 } from '@blueprintjs/select'
import { useApi } from '../contexts/ApiProvider'
import { useContext, useState, useEffect, useMemo, useCallback } from 'react'
import { WindowContext } from '../contexts/WindowProvider'
import { useNavigate, useParams } from 'react-router-dom'
import { SurveyContext } from '../contexts/SurveyProvider'

import 'normalize.css'
import '@blueprintjs/core/lib/css/blueprint.css'
import '@blueprintjs/icons/lib/css/blueprint-icons.css'
import '@blueprintjs/select/lib/css/blueprint-select.css'
import './Upload.css'

function AddDataToSurvey() {
  const api = useApi()
  const { surveyId } = useParams()
  const navigate = useNavigate()
  
  // Context hooks
  const { currentSurvey, setSurveys, setCurrentSurvey } = useContext(WindowContext)
  const { variables, setVariables, setPrevVariables } = useContext(SurveyContext)
  
  // States
  const [loading, setLoading] = useState(false)
  const [error, setError] = useState()
  const [selectedFile, setSelectedFile] = useState(null)
  const [fileInputText, setFileInputText] = useState('Choose a file...')
  const [variableId, setVariableId] = useState('')
  const [segmentationColumns, setSegmentationColumns] = useState([])
  const [importSegmentations, setImportSegmentations] = useState(false)

  const [previewLoading, setPreviewLoading] = useState(false)
  const [previewData, setPreviewData] = useState(null)
  const [previewError, setPreviewError] = useState(null)

  const fetchPreview = useCallback(
    async file => {
      setPreviewLoading(true)
      setPreviewError(null)
      setPreviewData(null)
      try {
        const formData = new FormData()
        formData.append('file', file)
        const response = await api.post(
          `/survey/${surveyId}/add_data/preview`,
          formData
        )
        if (response.ok) {
          setPreviewData(response.body)
        } else {
          const body = response.body || {}
          const message =
            body.error ||
            body.messages?.json?.file ||
            body.messages?.json?.upload ||
            'Could not read file.'
          setPreviewError(
            typeof message === 'string' ? message : JSON.stringify(message)
          )
        }
      } catch (err) {
        setPreviewError('Network error while reading the file.')
      } finally {
        setPreviewLoading(false)
      }
    },
    [api, surveyId]
  )

  const handleFileSelect = ev => {
    const file = ev.target.files[0]
    if (file) {
      setSelectedFile(file)
      setFileInputText(file.name)
      setError(undefined)
      setVariableId('')
      setSegmentationColumns([])
      setImportSegmentations(false)
      fetchPreview(file)
    }
    ev.target.value = ''
  }

  const fileHeaderNames = useMemo(
    () => new Set((previewData?.columns || []).map(c => c.name)),
    [previewData]
  )

  const joinableVariables = useMemo(
    () =>
      (variables || []).filter(v => fileHeaderNames.has(v.name)),
    [variables, fileHeaderNames]
  )

  const segmentationCandidateNames = useMemo(
    () =>
      (previewData?.columns || [])
        .filter(c => c.is_segmentation_candidate)
        .map(c => c.name),
    [previewData]
  )

  useEffect(() => {
    if (!previewData) return
    setSegmentationColumns(prev =>
      prev.filter(c => previewData.columns.some(col => col.name === c))
    )
    if (variableId) {
      const stillValid = joinableVariables.some(
        v => String(v.id) === String(variableId)
      )
      if (!stillValid) setVariableId('')
    }
  }, [previewData, joinableVariables, variableId])

  const handleSubmit = async () => {
    if (!selectedFile) {
      setError('Please select a file')
      return
    }

    setLoading(true)
    setError(undefined)

    try {
      const formData = new FormData()
      formData.append('file', selectedFile)
      if (variableId) {
        formData.append('variable_id', variableId)
      }
      segmentationColumns.forEach(col => {
        formData.append('segmentation_columns', col)
      })

      const response = await api.post(`/survey/${surveyId}/add_data`, formData)

      if (response.ok) {
        navigate('/tasks')
      } else {
        const errorBody = response.body
        if (errorBody?.error) {
          setError(errorBody.error)
        } else if (errorBody?.messages?.json) {
          const errorMessages = Object.entries(errorBody.messages.json)
            .map(([key, value]) => `${key}: ${value}`)
            .join(', ')
          setError(errorMessages)
        } else if (response.status === 415) {
          setError('Unsupported file format.')
        } else if (response.status === 404) {
          setError('Survey or join column not found.')
        } else {
          setError('An error occurred while adding data.')
        }
      }
    } catch (err) {
      console.error('Error adding columns:', err)
      setError('Network error occurred. Please try again.')
    } finally {
      setLoading(false)
    }
  }

  const renderOption = (name, { handleClick, modifiers }) => {
    if (!modifiers.matchesPredicate) return null
    const isSelected = segmentationColumns.includes(name)
    return (
      <MenuItem
        key={name}
        text={name}
        active={modifiers.active}
        disabled={modifiers.disabled}
        icon={isSelected ? 'tick' : 'blank'}
        onClick={handleClick}
        shouldDismissPopover={false}
      />
    )
  }

  const filterOption = (query, name) =>
    name.toLowerCase().indexOf(query.toLowerCase()) >= 0

  const handleItemSelect = name => {
    setSegmentationColumns(prev =>
      prev.includes(name) ? prev.filter(c => c !== name) : [...prev, name]
    )
  }

  const handleTagRemove = (_, index) => {
    setSegmentationColumns(prev => prev.filter((_, i) => i !== index))
  }

  if (!currentSurvey || !currentSurvey.has_data) {
    return (
      <div style={{ marginRight: 'auto', marginLeft: 'auto', width: '100%' }}>
        <div
          style={{
            display: 'flex',
            marginTop: '64px',
            alignItems: 'center',
            flexDirection: 'column',
          }}
        >
          <H1>Add Data to Survey</H1>
          <p style={{ marginTop: '16px', textAlign: 'center' }}>
            {!currentSurvey
              ? 'Loading...'
              : 'Survey must have existing data before adding more data.'}
          </p>
        </div>
      </div>
    )
  }

  const showSelectors = !!previewData && !previewLoading
  const canSubmit = !loading && !!selectedFile && showSelectors
  const joinHelper =
    variableId === ''
      ? 'Uploaded rows will be appended in file order. Ensure row count and order match the existing data.'
      : `Uploaded file must contain a column named "${
          joinableVariables.find(v => String(v.id) === String(variableId))?.name ||
          ''
        }" with matching values.`

  return (
    <div style={{ marginRight: 'auto', marginLeft: 'auto', width: '100%' }}>
      <div
        style={{
          display: 'flex',
          marginTop: '64px',
          alignItems: 'center',
          flexDirection: 'column',
        }}
      >
        <H1 style={{ marginBottom: '16px' }}>Add Data to Survey</H1>

        <div id="upload-info-text" style={{ marginBottom: '24px', maxWidth: '600px' }}>
          <p>
            Add new columns to your existing survey data. Upload a file first;
            the join column and segmentation options will populate from the
            file's headers.
          </p>
          <ul>
            <li>
              This survey currently has <b>{currentSurvey.n} respondents</b> and{' '}
              <b>{currentSurvey.num_variables} variables</b>
            </li>
            <li>
              Only rows with matching join values will have data added
            </li>
          </ul>
        </div>

        <div style={{ width: '400px' }}>
          <FormGroup
            label="Upload file:"
            helperText="Accepted formats: .csv, .xlsx, .sav"
            style={{ marginBottom: '16px' }}
          >
            <FileInput
              inputProps={{
                accept: '.sav,.csv,.xlsx',
                multiple: false,
              }}
              large
              fill
              text={fileInputText}
              buttonText="Browse"
              onInputChange={handleFileSelect}
              disabled={loading || previewLoading}
            />
          </FormGroup>

          {previewLoading && (
            <div
              style={{
                display: 'flex',
                alignItems: 'center',
                gap: '8px',
                marginBottom: '16px',
              }}
            >
              <Spinner size={16} />
              <span>Reading file...</span>
            </div>
          )}

          {previewError && (
            <p
              style={{
                color: '#AC2F33',
                marginBottom: '16px',
                fontWeight: 'bold',
                textAlign: 'center',
              }}
            >
              {previewError}
            </p>
          )}

          {showSelectors && (
            <>
              <FormGroup
                label="Select column to join on:"
                helperText={joinHelper}
                style={{ marginBottom: '16px' }}
              >
                <HTMLSelect
                  large
                  fill
                  value={variableId}
                  onChange={e => setVariableId(e.target.value)}
                  disabled={loading}
                >
                  <option value="">
                    No join column — data is in row order
                  </option>
                  {joinableVariables.map(variable => (
                    <option key={variable.id} value={variable.id}>
                      {variable.name}{' '}
                      {variable.label && `(${variable.label})`}
                    </option>
                  ))}
                </HTMLSelect>
              </FormGroup>

              <Switch
                checked={importSegmentations}
                onChange={e => {
                  const next = e.target.checked
                  setImportSegmentations(next)
                  if (!next) {
                    setSegmentationColumns([])
                  }
                }}
                label="Import Segmentations from this file"
                disabled={loading}
                style={{ marginBottom: importSegmentations ? '8px' : '24px' }}
              />

              {importSegmentations && (
                <FormGroup
                  label="Columns to import as Segmentations:"
                  helperText="Showing integer-valued columns with 2-20 unique values and no missing data — the typical shape of a segmentation."
                  style={{ marginBottom: '24px' }}
                >
                  {segmentationCandidateNames.length === 0 ? (
                    <div
                      style={{
                        padding: '12px',
                        backgroundColor: '#F5F8FA',
                        borderRadius: '4px',
                        border: '1px solid #D8E1E8',
                        color: '#5C7080',
                        fontSize: '13px',
                        display: 'flex',
                        alignItems: 'center',
                        gap: '8px',
                      }}
                    >
                      <Icon icon="info-sign" />
                      No segmentation candidates detected in this file.
                    </div>
                  ) : (
                    <MultiSelect2
                      items={segmentationCandidateNames}
                      itemRenderer={renderOption}
                      onItemSelect={handleItemSelect}
                      onRemove={handleTagRemove}
                      itemPredicate={filterOption}
                      selectedItems={segmentationColumns}
                      tagRenderer={name => name}
                      tagInputProps={{
                        tagProps: { intent: 'primary' },
                        disabled: loading,
                      }}
                      noResults={
                        <MenuItem disabled text="No matching columns." />
                      }
                      resetOnSelect={true}
                      placeholder="Select columns..."
                      fill
                    />
                  )}
                </FormGroup>
              )}
            </>
          )}
        </div>

        <Button
          large
          intent="primary"
          text={loading ? 'Adding data...' : 'Add Data'}
          onClick={handleSubmit}
          disabled={!canSubmit}
        />

        {error && (
          <p
            style={{
              color: '#AC2F33',
              marginTop: '16px',
              fontWeight: 'bold',
              maxWidth: '600px',
              textAlign: 'center',
            }}
          >
            {error}
          </p>
        )}
      </div>
    </div>
  )
}

export default AddDataToSurvey
